<script setup>
import { ref, computed, watch } from 'vue';

const props = defineProps({
  show: { type: Boolean, required: true },
  stage: { type: Object, default: () => ({}) },
  // All stages of the current pipeline so user can pick a target.
  pipelineStages: { type: Array, default: () => [] },
  // Number of active cards in the stage being deleted (drives whether to show
  // the picker or skip directly to confirm).
  activeCardsCount: { type: Number, default: 0 },
});
const emit = defineEmits(['close', 'confirm']);

const targetStageId = ref(null);
const isWorking = ref(false);

const targetCandidates = computed(() =>
  props.pipelineStages
    .filter(s => s.id !== props.stage?.id)
    .sort((a, b) => a.position - b.position)
);

watch(
  () => props.show,
  val => {
    if (val) {
      targetStageId.value = null;
      isWorking.value = false;
    }
  }
);

const needsTarget = computed(() => props.activeCardsCount > 0);
const canConfirm = computed(() => !needsTarget.value || !!targetStageId.value);

function confirm() {
  if (!canConfirm.value) return;
  isWorking.value = true;
  emit('confirm', { targetStageId: targetStageId.value });
}
</script>

<template>
  <div
    v-if="show"
    class="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    @click.self="$emit('close')"
  >
    <div class="bg-n-solid-1 rounded-xl border border-n-weak w-full max-w-md p-5 space-y-4">
      <h2 class="text-lg font-semibold text-n-slate-12">
        Excluir etapa "{{ stage?.name }}"
      </h2>

      <div v-if="needsTarget" class="space-y-3">
        <p class="text-sm text-n-slate-11">
          Esta etapa tem
          <strong class="text-n-slate-12">{{ activeCardsCount }}</strong>
          card(s) ativo(s). Escolha para qual etapa eles devem ser movidos
          antes da exclusão.
        </p>

        <div class="space-y-1">
          <label class="text-xs text-n-slate-11">Mover cards para:</label>
          <select
            v-model="targetStageId"
            class="w-full px-3 py-2 rounded border border-n-weak bg-transparent text-n-slate-12 text-sm"
          >
            <option :value="null">Selecione…</option>
            <option v-for="s in targetCandidates" :key="s.id" :value="s.id">
              {{ s.name }}
            </option>
          </select>
        </div>

        <p v-if="targetCandidates.length === 0" class="text-xs text-red-600">
          Não existem outras etapas neste pipeline. Crie uma etapa de destino
          primeiro.
        </p>
      </div>

      <p v-else class="text-sm text-n-slate-11">
        Esta etapa está vazia. Confirma a exclusão? Esta ação não pode ser
        desfeita.
      </p>

      <div class="flex gap-2 justify-end pt-2 border-t border-n-weak">
        <button
          class="px-3 py-1.5 rounded border border-n-weak text-sm text-n-slate-11 hover:bg-n-alpha-1"
          :disabled="isWorking"
          @click="$emit('close')"
        >
          Cancelar
        </button>
        <button
          class="px-3 py-1.5 rounded bg-red-600 hover:bg-red-700 text-white text-sm disabled:opacity-50"
          :disabled="!canConfirm || isWorking"
          @click="confirm"
        >
          {{ isWorking ? 'Excluindo…' : (needsTarget ? 'Mover e excluir' : 'Excluir') }}
        </button>
      </div>
    </div>
  </div>
</template>
